import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation } from '@tanstack/react-query'
import KNClipboardField from 'components/KNClipboardField/KNClipboardField'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNForm from 'components/KNForm/KNForm'
import KNFormPhone from 'components/KNForm/KNFormPhone'
import KNFormText from 'components/KNForm/KNFormText'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { regexEmail } from 'global/helpers/validators'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { formatParkingLocationDescription } from './TripDetails.helpers'
import { shareParkingLocationViaEmail, shareParkingLocationViaSms } from './TripDetails.service'
import type { ParkingLocation, ShareParkingLocationViaEmailFormValues, ShareParkingLocationViaSmsFormValues } from './TripDetails.types'

interface ParkingLocationShareDialogPayload {
  trip: TripData
  parkingLocation: ParkingLocation
  weblinkToken?: string
}

interface ParkingLocationShareDialogProps {
  payload: ParkingLocationShareDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const ParkingLocationShareDialog = ({ payload, open, onAction, onClose }: ParkingLocationShareDialogProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const [parkingLocationDetails, setParkingLocationDetails] = useState('')
  const {
    handleSubmit: handleSubmitEmail,
    reset: resetEmail,
    control: controlEmail,
    formState: formStateEmail,
    setError: setErrorEmail,
  } = useForm<ShareParkingLocationViaEmailFormValues>()
  const {
    handleSubmit: handleSubmitSms,
    reset: resetSms,
    control: controlSms,
    formState: formStateSms,
    getValues: getValuesSms,
    setError: setErrorSms,
  } = useForm<ShareParkingLocationViaSmsFormValues>()
  const shareViaEmailMutation = useMutation({
    mutationFn: (data: ShareParkingLocationViaEmailFormValues) =>
      shareParkingLocationViaEmail(
        payload.trip.entityId,
        payload.parkingLocation.entityId,
        data,
        payload.weblinkToken,
      ),
  })
  const shareViaSmsMutation = useMutation({
    mutationFn: (data: ShareParkingLocationViaSmsFormValues) =>
      shareParkingLocationViaSms(
        payload.trip.entityId,
        payload.parkingLocation.entityId,
        data,
        payload.weblinkToken,
      ),
  })

  useEffect(() => {
    if (open) {
      resetEmail(
        processDefaultValues({
          email: payload.trip.assignedDriver?.email,
        }),
      )
      resetSms(
        processDefaultValues({
          phoneNumber: payload.trip.assignedDriver?.contactNumber,
        }),
      )
    }
  }, [open])

  useEffect(() => {
    const parkingLocationType = t(`screens.cs.trip_details.parking_locations.${payload.parkingLocation.type}`)
    const parkingLocationDetails = [
      `${payload.parkingLocation.properties.title} (${parkingLocationType})`,
      payload.parkingLocation.properties.address,
    ]
    if (payload.parkingLocation.properties.long_description) {
      parkingLocationDetails.push(formatParkingLocationDescription(payload.parkingLocation.properties.long_description))
    }
    setParkingLocationDetails(parkingLocationDetails.join('\n'))
  }, [payload])

  const onSubmitEmail: SubmitHandler<ShareParkingLocationViaEmailFormValues> = async (data: ShareParkingLocationViaEmailFormValues) => {
    await handleFormSubmission(() => shareViaEmailMutation.mutateAsync(data), {
      onError: (error) => setErrorEmail('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_parking_location_shared', [getRouteName(location.pathname), 'email'])
      },
    })
  }

  const onSubmitSms: SubmitHandler<ShareParkingLocationViaSmsFormValues> = async (data: ShareParkingLocationViaSmsFormValues) => {
    await handleFormSubmission(() => shareViaSmsMutation.mutateAsync(data), {
      onError: (error) => setErrorSms('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_parking_location_trip_shared', [getRouteName(location.pathname), 'sms'])
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      preventClosing={formStateEmail.isSubmitting || formStateSms.isSubmitting}
      title={`${payload.parkingLocation.properties.title} — ${t('screens.cs.trip_details.sidebar.share_parking_location')}`}
    >
      <KNDialogFormErrors errors={formStateEmail.errors?.root} />
      <KNDialogFormErrors errors={formStateSms.errors?.root} />
      <Stack spacing={2}>
        <KNClipboardField
          value={parkingLocationDetails}
          multiline
          rows={parkingLocationDetails.split('\n').length + 1}
        />
        <KNForm onSubmit={handleSubmitEmail(onSubmitEmail)}>
          <Stack
            data-test="email"
            spacing={{ xs: 0.5, sm: 1 }}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'start', sm: 'end' }}
          >
            <KNFormText
              name="email"
              label={t('screens.cs.trip_dashboard.share.via_email')}
              placeholder={t('screens.cs.trip_dashboard.share.email_placeholder')}
              control={controlEmail}
              disabled={formStateEmail.isSubmitting}
              rules={{
                pattern: {
                  value: regexEmail,
                  message: t('form.validation.invalid_email'),
                },
                required: t('form.validation.required'),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              color="secondary"
              disabled={formStateEmail.isSubmitting}
              loading={formStateEmail.isSubmitting}
            >
              {t('form.send')}
            </Button>
          </Stack>
        </KNForm>
        <KNForm onSubmit={handleSubmitSms(onSubmitSms)}>
          <Stack
            data-test="sms"
            spacing={{ xs: 0.5, sm: 1 }}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'start', sm: 'end' }}
          >
            <KNFormPhone
              name="phoneNumber"
              label={t('screens.cs.trip_dashboard.share.via_sms')}
              placeholder={t('screens.cs.trip_dashboard.share.phone_placeholder')}
              control={controlSms}
              disabled={formStateSms.isSubmitting}
              rules={{
                required: t('form.validation.required'),
              }}
              getValues={getValuesSms}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              color="secondary"
              disabled={formStateSms.isSubmitting}
              loading={formStateSms.isSubmitting}
            >
              {t('form.send')}
            </Button>
          </Stack>
        </KNForm>
      </Stack>
    </KNDialog>
  )
}

export default ParkingLocationShareDialog
